import React, { useEffect, useRef, useState } from "react";
import { Link, NavLink, useNavigate } from "react-router-dom";
import {
  Eye,
  LogOut,
  Moon,
  Palette,
  PanelLeft,
  PanelRight,
  Sun,
  User as UserIcon,
} from "lucide-react";
import { useAuth } from "../../context/AuthContext";
import { useTheme } from "../../context/ThemeContext";
import { formatAppVersion, useAppVersion } from "../ui/VersionFooter";

/**
 * The 46px chrome strip at the top of every screen.
 *
 * Layout, left to right:
 *   brand · destination nav · breadcrumb (title / subtitle) · meta pills
 *   ————— spacer —————
 *   page actions · pane toggles · theme cycler · avatar menu
 *
 * The shell supplies nothing in the actions area beyond the pane toggles and
 * the theme/avatar controls — everything else is the route's `actions` slot.
 *
 * `AppChromeBar` is the same chrome minus the per-page slots, rendered by the
 * app Layout for routes that do not mount a WorkbenchShell (profile, admin)
 * so the destinations are never unreachable.
 */

const THEME_LABELS = { light: "Light", dark: "Dark", island: "Island" };

function nextThemeName(theme) {
  if (theme === "light") return THEME_LABELS.dark;
  if (theme === "dark") return THEME_LABELS.island;
  return THEME_LABELS.light;
}

function ThemeIcon({ theme }) {
  if (theme === "light") return <Moon aria-hidden="true" />;
  if (theme === "dark") return <Palette aria-hidden="true" />;
  return <Sun aria-hidden="true" />;
}

function ChromeBrand() {
  return (
    <Link to="/" className="wb-brand" aria-label="CostPilot home">
      <img
        src={process.env.PUBLIC_URL + "/assets/app-logo.png"}
        alt=""
        className="wb-brand__logo"
      />
      <span className="wb-brand__text">CostPilot</span>
    </Link>
  );
}

/** Primary destinations. Admin-only pages live in the avatar menu. */
function ChromeNav() {
  return (
    <nav className="wb-nav" aria-label="Primary">
      <NavLink
        to="/"
        end
        className={({ isActive }) =>
          `wb-nav__item${isActive ? " wb-nav__item--on" : ""}`
        }
      >
        Estimate
      </NavLink>
      <NavLink
        to="/jobs"
        className={({ isActive }) =>
          `wb-nav__item${isActive ? " wb-nav__item--on" : ""}`
        }
      >
        Jobs
      </NavLink>
      <NavLink
        to="/trial"
        className={({ isActive }) =>
          `wb-nav__item${isActive ? " wb-nav__item--on" : ""}`
        }
      >
        Pricing trial
      </NavLink>
    </nav>
  );
}

const ADMIN_DESTINATIONS = [
  { to: "/admin/rate-cards", label: "Rate Cards" },
  { to: "/admin/materials", label: "Materials" },
  { to: "/admin/pricing-settings", label: "Pricing Settings" },
  { to: "/admin/users", label: "Users" },
];

function ChromeControls() {
  const auth = useAuth() || {};
  const {
    currentUser,
    isAdmin,
    actualIsAdmin,
    viewAsEstimator,
    toggleViewMode,
    logout,
  } = auth;
  const { theme, toggleTheme } = useTheme();
  const navigate = useNavigate();
  const [menuOpen, setMenuOpen] = useState(false);
  const menuRef = useRef(null);
  // The deployed build. It used to live in a footer under every screen; a
  // shell route no longer has one, so it reads out of here instead.
  const versionLabel = formatAppVersion(useAppVersion(Boolean(currentUser)));

  useEffect(() => {
    if (!menuOpen) return undefined;
    const onPointerDown = (event) => {
      if (menuRef.current && !menuRef.current.contains(event.target)) {
        setMenuOpen(false);
      }
    };
    const onKeyDown = (event) => {
      if (event.key === "Escape") setMenuOpen(false);
    };
    document.addEventListener("mousedown", onPointerDown);
    document.addEventListener("keydown", onKeyDown);
    return () => {
      document.removeEventListener("mousedown", onPointerDown);
      document.removeEventListener("keydown", onKeyDown);
    };
  }, [menuOpen]);

  const displayName =
    currentUser?.name ||
    currentUser?.username ||
    currentUser?.email?.split("@")[0] ||
    "User";
  const initial = (currentUser?.name || currentUser?.email || "U")
    .charAt(0)
    .toUpperCase();

  const handleLogout = () => {
    setMenuOpen(false);
    if (typeof logout === "function") logout();
    navigate("/login");
  };

  return (
    <div className="wb-controls">
      <button
        type="button"
        className="wb-iconbtn"
        onClick={toggleTheme}
        aria-label={`Switch to ${nextThemeName(theme)} theme`}
        title={`Switch to ${nextThemeName(theme)} theme`}
      >
        <ThemeIcon theme={theme} />
      </button>

      {currentUser ? (
        <div className="wb-avatar-menu" ref={menuRef}>
          <button
            type="button"
            className="wb-avatar-btn"
            onClick={() => setMenuOpen((open) => !open)}
            aria-haspopup="menu"
            aria-expanded={menuOpen}
            aria-label={`Account menu for ${displayName}`}
            title={displayName}
          >
            <span className="wb-avatar">
              {currentUser.photo ? (
                <img
                  src={currentUser.photo}
                  alt=""
                  className="wb-avatar__img"
                />
              ) : (
                <span className="wb-avatar__initials">{initial}</span>
              )}
            </span>
          </button>

          {menuOpen ? (
            <div className="wb-menu" role="menu">
              <div className="wb-menu__header">
                <span className="wb-avatar wb-avatar--lg">
                  {currentUser.photo ? (
                    <img
                      src={currentUser.photo}
                      alt=""
                      className="wb-avatar__img"
                    />
                  ) : (
                    <span className="wb-avatar__initials">{initial}</span>
                  )}
                </span>
                <span className="wb-menu__identity">
                  <span className="wb-menu__name">
                    <span className="wb-menu__display">{displayName}</span>
                    <span
                      className={`wb-menu__role${
                        actualIsAdmin ? " wb-menu__role--admin" : ""
                      }`}
                    >
                      {actualIsAdmin ? "Admin" : "Estimator"}
                    </span>
                  </span>
                  <span className="wb-menu__email">
                    {currentUser.email || ""}
                  </span>
                  {/* A long branch name ellipsises in a 240px menu, so the
                      title carries the whole string as well as naming it. */}
                  {versionLabel ? (
                    <span
                      className="wb-menu__version"
                      title={`Deployed build — ${versionLabel}`}
                    >
                      {versionLabel}
                    </span>
                  ) : null}
                </span>
              </div>

              <div className="wb-menu__group">
                <span className="wb-menu__caption">Go to</span>
                <Link
                  to="/"
                  className="wb-menu__item"
                  role="menuitem"
                  onClick={() => setMenuOpen(false)}
                >
                  Estimate workbench
                </Link>
                <Link
                  to="/jobs"
                  className="wb-menu__item"
                  role="menuitem"
                  onClick={() => setMenuOpen(false)}
                >
                  Saved jobs
                </Link>
                <Link
                  to="/trial"
                  className="wb-menu__item"
                  role="menuitem"
                  onClick={() => setMenuOpen(false)}
                >
                  Pricing trial
                </Link>
              </div>

              {isAdmin ? (
                <div className="wb-menu__group">
                  <span className="wb-menu__caption">Admin</span>
                  {ADMIN_DESTINATIONS.map((destination) => (
                    <Link
                      key={destination.to}
                      to={destination.to}
                      className="wb-menu__item"
                      role="menuitem"
                      onClick={() => setMenuOpen(false)}
                    >
                      {destination.label}
                    </Link>
                  ))}
                </div>
              ) : null}

              <div className="wb-menu__group">
                <Link
                  to="/profile"
                  className="wb-menu__item"
                  role="menuitem"
                  onClick={() => setMenuOpen(false)}
                >
                  <UserIcon className="wb-menu__icon" aria-hidden="true" />
                  Profile
                </Link>
                {actualIsAdmin ? (
                  <button
                    type="button"
                    className="wb-menu__item"
                    role="menuitem"
                    onClick={() => {
                      if (typeof toggleViewMode === "function")
                        toggleViewMode();
                      setMenuOpen(false);
                    }}
                  >
                    <Eye className="wb-menu__icon" aria-hidden="true" />
                    {viewAsEstimator
                      ? "Exit estimator view"
                      : "View as estimator"}
                  </button>
                ) : null}
                <button
                  type="button"
                  className="wb-menu__item"
                  role="menuitem"
                  onClick={() => {
                    toggleTheme();
                    setMenuOpen(false);
                  }}
                >
                  <span className="wb-menu__icon">
                    <ThemeIcon theme={theme} />
                  </span>
                  Switch to {nextThemeName(theme)} theme
                </button>
                <button
                  type="button"
                  className="wb-menu__item wb-menu__item--danger"
                  role="menuitem"
                  onClick={handleLogout}
                >
                  <LogOut className="wb-menu__icon" aria-hidden="true" />
                  Log out
                </button>
              </div>
            </div>
          ) : null}
        </div>
      ) : (
        <Link to="/login" className="wb-tbtn wb-tbtn--edge">
          Sign in
        </Link>
      )}
    </div>
  );
}

function WorkbenchTopBar({
  variant = "estimate",
  title = "",
  subtitle = null,
  meta = null,
  actions = null,
  showRailToggle = false,
  railOpen = true,
  onToggleRail,
  showInspectorToggle = false,
  inspectorOpen = true,
  onToggleInspector,
  inspectorToggleLabel = "Cost trace",
}) {
  return (
    <header className="wb-topbar" data-variant={variant}>
      <ChromeBrand />
      <ChromeNav />

      {showRailToggle ? (
        <button
          type="button"
          className={`wb-iconbtn${railOpen ? " wb-iconbtn--on" : ""}`}
          onClick={onToggleRail}
          aria-label={railOpen ? "Hide list panel" : "Show list panel"}
          aria-pressed={railOpen}
          title="List panel"
        >
          <PanelLeft aria-hidden="true" />
        </button>
      ) : null}

      <div className="wb-crumb">
        {title ? <b className="wb-crumb__title">{title}</b> : null}
        {subtitle ? (
          <>
            <span className="wb-crumb__sep" aria-hidden="true">
              ·
            </span>
            <span className="wb-crumb__sub">{subtitle}</span>
          </>
        ) : null}
      </div>

      {meta ? <div className="wb-meta">{meta}</div> : null}

      <div className="wb-spacer" />

      {actions ? <div className="wb-actions">{actions}</div> : null}

      {showInspectorToggle ? (
        <button
          type="button"
          className={`wb-tbtn wb-tbtn--edge wb-trace-toggle${
            inspectorOpen ? " wb-tbtn--on" : ""
          }`}
          onClick={onToggleInspector}
          aria-pressed={inspectorOpen}
          title={`Toggle the ${inspectorToggleLabel} panel (Alt+T)`}
        >
          <PanelRight className="wb-tbtn__icon" aria-hidden="true" />
          <span className="wb-trace-toggle__label">{inspectorToggleLabel}</span>
        </button>
      ) : null}

      <ChromeControls />
    </header>
  );
}

/**
 * Chrome for routes that do not render a WorkbenchShell. Same bar, no
 * per-page slots — keeps brand, destinations, theme and account reachable.
 */
export function AppChromeBar({ title = "" }) {
  return (
    <header className="wb-topbar wb-topbar--standalone">
      <ChromeBrand />
      <ChromeNav />
      {title ? (
        <div className="wb-crumb">
          <b className="wb-crumb__title">{title}</b>
        </div>
      ) : null}
      <div className="wb-spacer" />
      <ChromeControls />
    </header>
  );
}

export default WorkbenchTopBar;
